import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { X, Send } from "lucide-react";

interface QuoteFormProps {
  language: 'en' | 'de';
  onClose: () => void;
}

export const QuoteForm = ({ language, onClose }: QuoteFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: '',
    website: '' // Honeypot field
  });

  const executiveRoles = [
    "Chief Executive Officer (CEO)",
    "Chief Information Officer (CIO)",
    "Chief Technology Officer (CTO)",
    "Chief Digital Officer (CDO)",
    "Chief Operating Officer (COO)",
    "Chief Financial Officer (CFO)",
    "Chief Strategy Officer (CSO)",
    "Vice President (VP)",
    "Managing Director (MD)",
    "Board Member"
  ];

  const content = {
    en: {
      title: "Request a Quote",
      subtitle: "Schedule your strategic sounding meeting",
      processInfo: "After submitting this request, I will schedule an initial sounding meeting with you. Following our discussion and two touchpoints, you will receive a customized quote for your executive consulting program.",
      fields: {
        name: "Full Name",
        email: "Email Address",
        company: "Company",
        role: "Your Executive Role",
        message: "Strategic Challenges (minimum 30 words)"
      },
      errors: {
        network: "Network error. Please check your connection and try again.",
        server: "Server error. Please try again in a few moments.",
        timeout: "Request timed out. Please try again.",
        unknown: "An unexpected error occurred. Please try again or contact us directly."
      },
      placeholders: {
        name: "John Smith",
        email: "john.smith@company.com",
        company: "Your Company Name",
        role: "Select your role",
        message: "Describe your strategic objectives, challenges, or areas where you're seeking guidance. Please provide at least 30 words to help me understand your needs better..."
      },
      submit: "Submit Request",
      cancel: "Cancel",
      success: "Thank you! Your quote request has been received. I'll reach out within 24 hours to schedule our sounding meeting.",
      error: "Something went wrong. Please try again or contact directly.",
      validation: {
        messageMinWords: "Please provide at least 30 words describing your strategic challenges.",
        allFieldsRequired: "All fields are required.",
        invalidEmail: "Please enter a valid email address."
      }
    },
    de: {
      title: "Angebot anfragen",
      subtitle: "Vereinbaren Sie Ihr strategisches Sondierungsgespräch",
      processInfo: "Nach Einreichung dieser Anfrage werde ich ein erstes Sondierungsgespräch mit Ihnen vereinbaren. Nach unserer Besprechung und zwei Kontaktpunkten erhalten Sie ein maßgeschneidertes Angebot für Ihr Executive Consulting Programm.",
      fields: {
        name: "Vollständiger Name",
        email: "E-Mail-Adresse",
        company: "Unternehmen",
        role: "Ihre Executive Rolle",
        message: "Strategische Herausforderungen (mindestens 30 Wörter)"
      },
      errors: {
        network: "Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
        server: "Serverfehler. Bitte versuchen Sie es in einigen Augenblicken erneut.",
        timeout: "Zeitüberschreitung der Anfrage. Bitte versuchen Sie es erneut.",
        unknown: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt."
      },
      placeholders: {
        name: "Max Mustermann",
        email: "max.mustermann@unternehmen.de",
        company: "Ihr Unternehmensname",
        role: "Wählen Sie Ihre Rolle",
        message: "Beschreiben Sie Ihre strategischen Ziele, Herausforderungen oder Bereiche, in denen Sie Beratung suchen. Bitte geben Sie mindestens 30 Wörter an, damit ich Ihre Bedürfnisse besser verstehen kann..."
      },
      submit: "Anfrage senden",
      cancel: "Abbrechen",
      success: "Vielen Dank! Ihre Angebotsanfrage wurde erhalten. Ich melde mich innerhalb von 24 Stunden, um unser Sondierungsgespräch zu vereinbaren.",
      error: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut oder kontaktieren Sie mich direkt.",
      validation: {
        messageMinWords: "Bitte geben Sie mindestens 30 Wörter an, um Ihre strategischen Herausforderungen zu beschreiben.",
        allFieldsRequired: "Alle Felder sind erforderlich.",
        invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
      }
    }
  };

  const t = content[language];

  const validateForm = (): boolean => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Validation Error",
        description: t.validation.invalidEmail,
        variant: "destructive",
      });
      return false;
    }

    // Validate all fields are filled
    if (!formData.name || !formData.email || !formData.company || !formData.role || !formData.message) {
      toast({
        title: "Validation Error",
        description: t.validation.allFieldsRequired,
        variant: "destructive",
      });
      return false;
    }

    // Validate minimum word count (30 words)
    const wordCount = formData.message.trim().split(/\s+/).length;
    if (wordCount < 30) {
      toast({
        title: "Validation Error",
        description: t.validation.messageMinWords,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call edge function for server-side validation and duplicate detection
      const { data, error } = await supabase.functions.invoke('submit-quote', {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          company: formData.company.trim(),
          role: formData.role,
          message: formData.message.trim(),
          language: language,
          honeypot: formData.website // Send honeypot field
        }
      });

      // Check if the response indicates an error
      if (error) {
        console.error('Submission error:', error);
        
        // The edge function may return structured error data even with error status
        // Try to extract meaningful error information
        const errorData = data || {};
        
        // Handle specific error types based on error data
        if (errorData.error === 'duplicate') {
          toast({
            title: language === 'en' ? "Already Submitted" : "Bereits eingereicht",
            description: errorData.message || (language === 'en' 
              ? "We received your request and will contact you within 24 hours"
              : "Wir haben Ihre Anfrage erhalten und werden Sie innerhalb von 24 Stunden kontaktieren"),
          });
          onClose();
          return;
        }
        
        if (errorData.error === 'rate_limit') {
          toast({
            title: language === 'en' ? "Too Many Requests" : "Zu viele Anfragen",
            description: errorData.message || (language === 'en'
              ? "Please wait before submitting another request"
              : "Bitte warten Sie, bevor Sie eine weitere Anfrage einreichen"),
            variant: "destructive",
          });
          return;
        }
        
        if (errorData.error === 'honeypot') {
          // Silently fail for bot detection
          console.log('Honeypot triggered');
          onClose();
          return;
        }
        
        // Handle validation errors
        if (errorData.error === 'validation') {
          toast({
            title: language === 'en' ? "Validation Error" : "Validierungsfehler",
            description: errorData.message || (language === 'en'
              ? "Please check your input and try again"
              : "Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut"),
            variant: "destructive",
          });
          return;
        }
        
        // Generic error with message from server if available
        if (errorData.message) {
          toast({
            title: "Error",
            description: errorData.message,
            variant: "destructive",
          });
          return;
        }
        
        // Fallback to generic error handling based on error properties
        if (error.message?.includes('fetch') || error.message?.includes('network')) {
          toast({
            title: "Error",
            description: t.errors.network,
            variant: "destructive",
          });
          return;
        }
        
        if (error.message?.includes('timeout')) {
          toast({
            title: "Error",
            description: t.errors.timeout,
            variant: "destructive",
          });
          return;
        }
        
        throw error;
      }
      
      toast({
        title: language === 'en' ? "Request Submitted" : "Anfrage gesendet",
        description: t.success,
      });
      
      onClose();
    } catch (error: any) {
      console.error('Error submitting quote request:', error);
      
      // Determine specific error message with more context
      let errorTitle = "Error";
      let errorMessage = t.errors.unknown;
      
      // Check for specific HTTP status codes
      if (error?.status === 500 || error?.message?.includes('500')) {
        errorTitle = language === 'en' ? "Server Error" : "Serverfehler";
        errorMessage = t.errors.server;
      } else if (error?.status === 400) {
        errorTitle = language === 'en' ? "Invalid Request" : "Ungültige Anfrage";
        errorMessage = language === 'en'
          ? "Please check your input and try again"
          : "Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut";
      } else if (error?.status === 429) {
        errorTitle = language === 'en' ? "Rate Limit Exceeded" : "Ratenlimit überschritten";
        errorMessage = language === 'en'
          ? "Too many requests. Please try again later"
          : "Zu viele Anfragen. Bitte versuchen Sie es später erneut";
      } else if (error?.message?.includes('fetch') || error?.message?.includes('network') || error?.message?.includes('Failed to fetch')) {
        errorTitle = language === 'en' ? "Network Error" : "Netzwerkfehler";
        errorMessage = t.errors.network;
      } else if (error?.message?.includes('timeout') || error?.message?.includes('timed out')) {
        errorTitle = language === 'en' ? "Timeout" : "Zeitüberschreitung";
        errorMessage = t.errors.timeout;
      } else if (error?.message && error.message !== 'Error') {
        // Use the error message if it's meaningful and not just "Error"
        errorMessage = error.message;
      }
      
      // Log detailed error for debugging
      console.error('Detailed error:', {
        status: error?.status,
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-elegant max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="gradient-hero p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-primary-foreground/10 transition-smooth"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-primary-foreground" />
          </button>
          <h2 className="text-3xl font-semibold text-primary-foreground mb-2">{t.title}</h2>
          <p className="text-primary-foreground/90">{t.subtitle}</p>
          <a 
            href="https://baltx.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-3 text-primary-foreground/90 hover:text-primary-foreground underline transition-smooth"
          >
            baltx.com
          </a>
        </div>

        {/* Process Info */}
        <div className="bg-accent/10 border-l-4 border-accent p-4 mx-6 mt-6 rounded">
          <p className="text-sm text-foreground/80">{t.processInfo}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t.fields.name}</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t.placeholders.name}
                className="transition-smooth focus:shadow-glow"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t.fields.email}</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={t.placeholders.email}
                className="transition-smooth focus:shadow-glow"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="company">{t.fields.company}</Label>
              <Input
                id="company"
                required
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                placeholder={t.placeholders.company}
                className="transition-smooth focus:shadow-glow"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t.fields.role}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleChange('role', value)}
                required
              >
                <SelectTrigger id="role" className="transition-smooth focus:shadow-glow">
                  <SelectValue placeholder={t.placeholders.role} />
                </SelectTrigger>
                <SelectContent className="bg-card z-50">
                  {executiveRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t.fields.message}</Label>
            <Textarea
              id="message"
              required
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder={t.placeholders.message}
              rows={6}
              className="transition-smooth focus:shadow-glow resize-none"
            />
          </div>

          {/* Honeypot field - hidden from humans, visible to bots */}
          <div className="absolute left-[-9999px] w-1 h-1 overflow-hidden" aria-hidden="true">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="text"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="flex gap-4 justify-end pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t.cancel}
            </Button>
            <Button
              type="submit"
              variant="hero"
              size="lg"
              disabled={isSubmitting}
              className="group"
            >
              {isSubmitting ? (
                language === 'en' ? 'Sending...' : 'Wird gesendet...'
              ) : (
                <>
                  {t.submit}
                  <Send className="h-4 w-4 transition-bounce group-hover:translate-x-1" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};